import { useTranslation } from 'react-i18next';

import {
  Text,
  Divider,
  ColorsV2,
  Flex,
  Box,
  Chip,
  useThemeColors,
  Button,
  Pane,
  spaces,
} from '@electricjs/arc';

import formatCurrency from '@/helpers/formatCurrency';
import { StorefrontSteps } from '@/types/storefront';

import { useStorefront } from '../../StorefrontProvider';
import { isWarehouseAsset } from '../../utils';

type CartSummaryProps = {
  hasEditButton?: boolean;
};

const CartSummary = ({ hasEditButton }: CartSummaryProps) => {
  const { t } = useTranslation('storefront');
  const {
    itemsInCart,
    catalogCart,
    assetCart,
    storefrontStep,
    setStorefrontStep,
  } = useStorefront();

  const [textChipColor, backgroundChipColor] = useThemeColors([
    ColorsV2.INFO,
    ColorsV2.INFO_LIGHTEST,
  ]);

  const warehouseAssets = assetCart.filter(isWarehouseAsset);
  const nonWarehouseAssets = assetCart.filter(
    entry => !isWarehouseAsset(entry)
  );

  return (
    <Pane stack elevated padding={spaces.space_l}>
      <Flex
        columnGap="1.5rem"
        rowGap="0.5rem"
        flexWrap="wrap"
        vAlignContent="center">
        {/* Title */}
        <Text variant="heading-1">
          {storefrontStep === StorefrontSteps.ORDER_PLACED
            ? t('sections.cartSummary.orderedItems')
            : t('sections.cartSummary.cart')}
        </Text>
        <Text mt="0.5rem">
          ({itemsInCart}{' '}
          {itemsInCart === 1
            ? t('sections.cartSummary.item_one')
            : t('sections.cartSummary.item_other')}
          )
        </Text>
        {/* Edit Button */}
        {hasEditButton && (
          <Button
            id="edit-cart-button"
            variant="link"
            onClick={() => setStorefrontStep(StorefrontSteps.CART)}
            ml="auto"
            mr="1rem"
            mt="0.5rem">
            {t('sections.cartSummary.edit')}
          </Button>
        )}
      </Flex>

      <Flex
        vertical
        rowGap="1rem"
        width="100%"
        minWidth="15rem"
        mt={spaces.space_m_rem}>
        {/* Warehouse assets */}
        {warehouseAssets.length > 0 && (
          <Flex vertical rowGap="1rem">
            <Text variant="label-large">
              {t('sections.cartSummary.warehouseAssetsTitle')}
            </Text>
            <Chip
              id="warehouse-shipping-method-chip"
              textColor={textChipColor}
              backgroundColor={backgroundChipColor}
              variant="small">
              {t('sections.cartSummary.deliveryMethod')}:{' '}
              {t('sections.cartSummary.deliveryMethodWarehouseShip')}
            </Chip>
            {warehouseAssets.map(entry => {
              return (
                <Box key={entry.item.id}>
                  <Text>{entry.item.name}</Text>
                  <Text>{t('sections.cartSummary.qty')} 1</Text>
                </Box>
              );
            })}
          </Flex>
        )}

        {/* Divider */}
        {warehouseAssets.length > 0 &&
          (nonWarehouseAssets.length > 0 || catalogCart.length > 0) && (
            <Divider color={ColorsV2.GRAY_LIGHT} />
          )}

        {/* Non-warehouse assets */}
        {nonWarehouseAssets.length > 0 && (
          <Flex vertical rowGap="1rem">
            <Text variant="label-large">
              {t('sections.cartSummary.companyHeldAssetsTitle')}
            </Text>
            <Chip
              id="self-ship-chip"
              textColor={textChipColor}
              backgroundColor={backgroundChipColor}
              variant="small">
              {t('sections.cartSummary.deliveryMethod')}:{' '}
              {t('sections.cartSummary.deliveryMethodSelfShip')}
            </Chip>
            {nonWarehouseAssets.map(entry => {
              return (
                <Box key={entry.item.id}>
                  <Text>{entry.item.name}</Text>
                  <Text>{t('sections.cartSummary.qty')} 1</Text>
                </Box>
              );
            })}
          </Flex>
        )}

        {/* Divider */}
        {nonWarehouseAssets.length > 0 && catalogCart.length > 0 && (
          <Divider color={ColorsV2.GRAY_LIGHT} />
        )}

        {/* Catalog */}
        {catalogCart.length > 0 && (
          <Flex vertical rowGap="1rem">
            <Text variant="label-large">
              {t('sections.cartSummary.purchases')}
            </Text>
            {catalogCart.map(entry => {
              return (
                <Box key={entry.item.id}>
                  <Text>{entry.item.name}</Text>
                  <Text>
                    {t('sections.cartSummary.qty')} {entry.quantity},{' '}
                    {entry.item.price === undefined
                      ? t('sections.cartSummary.priceUnavailable')
                      : formatCurrency(
                          entry.item.price * entry.quantity,
                          entry.item.currencyCode
                        )}
                  </Text>
                  {/* Show warranty as item only if the warranty selected is different from the default warranty */}
                  {entry.selectedWarranty?.id &&
                    entry.selectedWarranty.id !==
                      entry.item.availableWarranties?.defaultWarrantyId && (
                      <Box mt="1rem">
                        <Text>{entry.selectedWarranty?.name}</Text>
                        <Text>
                          {t('sections.cartSummary.qty')} {entry.quantity},{' '}
                          {entry.selectedWarranty?.price === undefined
                            ? t('sections.cartSummary.priceUnavailable')
                            : formatCurrency(
                                entry.selectedWarranty.price * entry.quantity,
                                entry.selectedWarranty.currencyCode
                              )}
                        </Text>
                      </Box>
                    )}
                </Box>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Pane>
  );
};

export default CartSummary;
